import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { attendanceService } from "@/services/attendanceService";
import { 
  CreateShiftRequest, 
  CreateHolidayRequest, 
  CreateScheduleRequest, 
  BulkAssignScheduleRequest,
  CreateAbsenceRequest,
  CreateWorkstationEvaluationRequest,
  IncidentPerformanceScore,
  WorkstationEvaluationHistoryResponse,
} from "@/types/attendance";
import { toast } from "sonner";

interface ApiError {
  response?: {
    data?: {
      error?: string;
    };
  };
}

interface BulkAssignmentResult {
  status: "blocked" | "success" | string;
  error?: string;
}

interface AttendancePerformanceParams {
  slug: string;
  start_date?: string;
  end_date?: string;
  employee_id?: string;
}

export const useAttendance = (slug: string, start_date?: string, end_date?: string) => {
  const queryClient = useQueryClient();

  const shiftsQuery = useQuery({
    queryKey: ["attendance", "shifts", slug],
    queryFn: () => attendanceService.getShifts(slug),
    enabled: !!slug,
    select: (res) => res.data?.data || [],
  });

  const holidaysQuery = useQuery({
    queryKey: ["attendance", "holidays", slug],
    queryFn: () => attendanceService.getHolidays(slug),
    enabled: !!slug,
    select: (res) => res.data?.data || [],
  });

  const schedulesQuery = useQuery({
    queryKey: ["attendance", "schedules", slug, start_date, end_date],
    queryFn: () => attendanceService.getSchedules(slug, start_date, end_date),
    enabled: !!slug,
    select: (res) => res.data?.data || [],
  });

  const absencesQuery = useQuery({
    queryKey: ["attendance", "absences", slug, start_date, end_date],
    queryFn: () => attendanceService.getAbsences(slug, undefined, start_date, end_date),
    enabled: !!slug,
    select: (res) => res.data?.data || [],
  });

  const createShift = useMutation({
    mutationFn: (data: CreateShiftRequest) => attendanceService.createShift(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "shifts", slug] });
    },
  });

  const deleteShift = useMutation({
    mutationFn: (id: string) => attendanceService.deleteShift(id, slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "shifts", slug] });
      toast.success("Turno eliminado correctamente");
    },
    onError: () => {
      toast.error("Error al eliminar el turno");
    }
  });

  const createHoliday = useMutation({
    mutationFn: (data: CreateHolidayRequest) => attendanceService.createHoliday(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "holidays", slug] });
      toast.success("Festivo guardado correctamente");
    },
    onError: () => {
      toast.error("Error al guardar el festivo");
    }
  });

  const deleteHoliday = useMutation({
    mutationFn: (id: string) => attendanceService.deleteHoliday(id, slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "holidays", slug] });
      toast.success("Festivo eliminado");
    },
  });

  const createSchedule = useMutation({
    mutationFn: (data: CreateScheduleRequest) => attendanceService.createSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "schedules", slug] });
      toast.success("Turno asignado correctamente");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error || "Error al asignar turno");
    }
  });

  const bulkAssignSchedule = useMutation({
    mutationFn: (data: BulkAssignScheduleRequest) => attendanceService.bulkAssignSchedule(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "schedules", slug] });
      
      const results = (res.data?.data || []) as BulkAssignmentResult[];
      const blocked = results.filter((result) => result.status === "blocked");
      const success = results.filter((result) => result.status === "success");
      const holidayBlocked = blocked.filter((result) =>
        result.error?.toLowerCase().includes("festivo"),
      ).length;
      const absenceBlocked = blocked.length - holidayBlocked;
      
      if (blocked.length > 0) {
        const blockedSummary = [
          absenceBlocked > 0 ? `${absenceBlocked} por ausencia` : null,
          holidayBlocked > 0 ? `${holidayBlocked} por festivo` : null,
        ]
          .filter(Boolean)
          .join(" y ");

        toast.warning(
          `Asignación parcial: ${success.length} exitosas, ${blocked.length} bloqueadas${blockedSummary ? ` (${blockedSummary})` : ""}.`,
        );
      } else {
        toast.success("Asignación masiva completada");
      }
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error || "Error en asignación masiva");
    }
  });

  const deleteSchedule = useMutation({
    mutationFn: (id: string) => attendanceService.deleteSchedule(id, slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "schedules", slug] });
      toast.success("Asignación eliminada");
    },
  });

  const createAbsence = useMutation({
    mutationFn: (data: CreateAbsenceRequest) => attendanceService.createAbsence(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "absences", slug] });
      toast.success("Ausencia registrada correctamente");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error || "Error al registrar ausencia");
    }
  });

  const deleteAbsence = useMutation({
    mutationFn: (id: string) => attendanceService.deleteAbsence(id, slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "absences", slug] });
      toast.success("Ausencia eliminada");
    },
  });

  const performanceRankingQuery = useQuery({
    queryKey: ["attendance", "performance", "ranking", slug, start_date, end_date],
    queryFn: () => attendanceService.getPerformanceRanking(slug, start_date, end_date),
    enabled: !!slug,
    select: (res) => res.data?.data || [],
  });

  const createQualityIncident = useMutation({
    mutationFn: (data: Record<string, unknown>) => attendanceService.createQualityIncident(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "performance", "ranking", slug] });
      toast.success("Incidente registrado");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error || "Error al registrar incidente");
    }
  });

  const createWorkstationEvaluation = useMutation({
    mutationFn: (data: CreateWorkstationEvaluationRequest) =>
      attendanceService.createWorkstationEvaluation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "performance", "ranking", slug] });
      toast.success("Evaluación registrada");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error || "Error al registrar evaluación");
    }
  });

  const createProductionTask = useMutation({
    mutationFn: (data: Record<string, unknown>) => attendanceService.createProductionTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "performance", "ranking", slug] });
      toast.success("Tarea registrada");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error || "Error al registrar tarea");
    }
  });

  return {
    shifts: shiftsQuery.data,
    isLoadingShifts: shiftsQuery.isLoading,
    holidays: holidaysQuery.data,
    isLoadingHolidays: holidaysQuery.isLoading,
    schedules: schedulesQuery.data,
    isLoadingSchedules: schedulesQuery.isLoading,
    createShift,
    deleteShift,
    createHoliday,
    deleteHoliday,
    createSchedule,
    bulkAssignSchedule,
    deleteSchedule,
    absences: absencesQuery.data,
    isLoadingAbsences: absencesQuery.isLoading,
    createAbsence,
    deleteAbsence,
    performanceRanking: performanceRankingQuery.data,
    isLoadingPerformance: performanceRankingQuery.isLoading,
    createWorkstationEvaluation,
    createQualityIncident,
    createProductionTask,
  };
};

export const useAttendancePerformance = ({
  slug,
  start_date,
  end_date,
  employee_id,
}: AttendancePerformanceParams) => {
  const queryClient = useQueryClient();

  const performanceRankingQuery = useQuery({
    queryKey: ["attendance", "performance", "ranking", slug, start_date, end_date],
    queryFn: () => attendanceService.getPerformanceRanking(slug, start_date, end_date),
    enabled: !!slug,
    select: (res) => res.data?.data || [],
  });

  const evaluationHistoryQuery = useQuery({
    queryKey: [
      "attendance",
      "performance",
      "history",
      slug,
      employee_id,
      start_date,
      end_date,
    ],
    queryFn: async (): Promise<WorkstationEvaluationHistoryResponse | null> => {
      if (!employee_id) return null;
      const response = await attendanceService.getWorkstationEvaluationHistory(
        slug,
        employee_id,
        start_date,
        end_date,
      );
      return response.data?.data || null;
    },
    enabled: !!slug && !!employee_id,
  });

  const incidentPerformanceQuery = useQuery({
    queryKey: [
      "attendance",
      "performance",
      "incident-score",
      slug,
      employee_id,
      start_date,
      end_date,
    ],
    queryFn: async (): Promise<IncidentPerformanceScore | null> => {
      if (!employee_id) return null;
      const response = await attendanceService.getIncidentPerformanceScore(
        slug,
        employee_id,
        start_date,
        end_date,
      );
      return response.data?.data || null;
    },
    enabled: !!slug && !!employee_id,
  });

  const invalidatePerformanceQueries = () =>
    queryClient.invalidateQueries({
      queryKey: ["attendance", "performance"],
    });

  const createWorkstationEvaluation = useMutation({
    mutationFn: (data: CreateWorkstationEvaluationRequest) =>
      attendanceService.createWorkstationEvaluation(data),
    onSuccess: () => {
      invalidatePerformanceQueries();
      toast.success("Evaluación registrada");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error || "Error al registrar evaluación");
    },
  });

  const createQualityIncident = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      attendanceService.createQualityIncident(data),
    onSuccess: () => {
      invalidatePerformanceQueries();
      toast.success("Incidente registrado");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error || "Error al registrar incidente");
    },
  });

  const createProductionTask = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      attendanceService.createProductionTask(data),
    onSuccess: () => {
      invalidatePerformanceQueries();
      toast.success("Tarea registrada");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error || "Error al registrar tarea");
    },
  });

  return {
    performanceRanking: performanceRankingQuery.data,
    isLoadingPerformance: performanceRankingQuery.isLoading,
    evaluationHistory: evaluationHistoryQuery.data,
    isLoadingEvaluationHistory: evaluationHistoryQuery.isLoading,
    incidentPerformanceScore: incidentPerformanceQuery.data,
    isLoadingIncidentPerformanceScore: incidentPerformanceQuery.isLoading,
    createWorkstationEvaluation,
    createQualityIncident,
    createProductionTask,
  };
};
